import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import MarketGrowthAdminApi from "../../../../../infrastructure/api/marketGrowthAdmin";
import { formatMoney } from "../../../partner-commission/utils/formatMoney";
import { getEnumLabel } from "../constants/commissionEnums";
import {
  PARTNER_STATUS_LABELS,
  getPartnerStatusChipColor,
  buildPartnerJoinLink,
  canShowPartnerJoinLink,
} from "../constants/partnerEnums";
import CopyTextField from "../../../partner-commission/components/CopyTextField";
import MarketGrowthDataTable from "./MarketGrowthDataTable";

const SummaryField = ({ label, value }) => (
  <>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </>
);

const PartnerTeamDialog = ({ open, onClose, partner }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [members, setMembers] = useState([]);

  const loadMembers = useCallback(async () => {
    if (!partner?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await MarketGrowthAdminApi.getPartnerTeamMembers(partner.id);
      const normalized = MarketGrowthAdminApi.normalize(
        res,
        "بارگذاری اعضای تیم ناموفق بود."
      );
      if (normalized.error) {
        setError(normalized.error);
        setMembers([]);
        return;
      }
      setMembers(Array.isArray(normalized.data) ? normalized.data : []);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [partner?.id]);

  useEffect(() => {
    if (open && partner?.id) {
      loadMembers();
    } else if (!open) {
      setMembers([]);
      setError(null);
    }
  }, [open, partner?.id, loadMembers]);

  const tableRows = useMemo(
    () =>
      members.map((row) => ({
        ...row,
        id: row.partnerId,
        nameLabel: row.displayName || "—",
        statusLabel: getEnumLabel(PARTNER_STATUS_LABELS, row.status),
        shareLabel: row.currentSharePercent ?? "—",
        totalCommissionLabel: formatMoney(row.totalCommissionGenerated),
        paidCommissionLabel: formatMoney(row.totalPaidCommission),
      })),
    [members]
  );

  const headCells = useMemo(
    () => [
      {
        id: "nameLabel",
        label: "نام / کاربر",
        render: (_, row) => (
          <>
            {row.displayName || "—"}
            <Box component="span" sx={{ color: "text.secondary", ml: 0.5 }}>
              ({row.userId})
            </Box>
          </>
        ),
      },
      { id: "mobileNumber", label: "موبایل" },
      {
        id: "statusLabel",
        label: "وضعیت",
        render: (_, row) => (
          <Chip
            size="small"
            label={getEnumLabel(PARTNER_STATUS_LABELS, row.status)}
            color={getPartnerStatusChipColor(row.status)}
          />
        ),
      },
      { id: "referralCode", label: "کد معرف" },
      { id: "shareLabel", label: "سهم (%)" },
      { id: "totalCommissionLabel", label: "کل پورسانت" },
      { id: "paidCommissionLabel", label: "پرداخت‌شده" },
    ],
    []
  );

  if (!partner) return null;

  const displayName =
    partner.displayName || partner.mobileNumber || `کاربر ${partner.userId}`;
  const partnerJoinLink = canShowPartnerJoinLink(partner)
    ? buildPartnerJoinLink(partner.referralCode)
    : "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>تیم {displayName}</DialogTitle>
      <DialogContent dividers>
        {partnerJoinLink ? (
          <Box sx={{ mb: 2 }}>
            <CopyTextField
              label="لینک ثبت‌نام زیرمجموعه"
              value={partnerJoinLink}
              copyMessage="لینک ثبت‌نام زیرمجموعه کپی شد."
            />
          </Box>
        ) : null}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <SummaryField
              label="کل اعضا"
              value={partner.teamMemberCount ?? 0}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryField
              label="اعضای فعال"
              value={partner.activeTeamMemberCount ?? 0}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryField
              label="کل پورسانت تیم"
              value={formatMoney(partner.teamTotalCommissionGenerated ?? 0)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryField
              label="پرداخت‌شده"
              value={formatMoney(partner.teamTotalPaidCommission ?? 0)}
            />
          </Grid>
        </Grid>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : members.length === 0 ? (
          <Alert severity="info">هنوز عضوی در این تیم ثبت نشده است.</Alert>
        ) : (
          <MarketGrowthDataTable
            rowKey="id"
            rows={tableRows}
            headCells={headCells}
            showFilter={false}
            showIndex={false}
            showPaging={members.length > 10}
            pageSize={100}
            tableMinWidth={700}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>بستن</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartnerTeamDialog;
